import {useEffect, useMemo, useRef} from "react";
import {useParams} from "react-router-dom";

import {PLACEHOLDERS} from "data/constants";
import {isUntouchedTextRecord, useEdits} from "contexts/edits";
import {useSetting} from "contexts/setting";
import {useChannelFromParams} from "hooks/useChannelFromParams";
import {useContentTypeFromParams} from "hooks/useContentTypeFromParams";
import {useProductFromParams} from "hooks/useProductFromParams";
import {homeScope} from "data/offering";

function getSocialVariantKey(productValue, typeValue) {
  if (productValue === "JB" && typeValue === "newsletter") return "JB_newsletter";
  if (productValue === "DN" && typeValue === "newsletter") return "DN_newsletter";
  if (productValue === "MP" && typeValue === "newsletter") return "MP_newsletter";
  if (productValue === "IJ" && typeValue === "java-annotated") return "IJ_newsletter";
  if (productValue === "TC" && typeValue === "newsletter") return "TC_newsletter";
  if (productValue === "PS" && typeValue === "php-annotated") return "PS_newsletter";
  return null;
}

function getAdsVariantKey(productValue, typeValue) {
  if (productValue === "JB" && typeValue === "ai") return "JB_ai";
  if (productValue === "JB" && typeValue === "deveco") return "JB_deveco";
  return null;
}

function getVariantPlaceholder(placeholders, key, variantKey) {
  if (!variantKey) {
    return placeholders[key];
  }

  return placeholders[`${key}_${variantKey}`] ?? placeholders[key];
}

/**
 * The placeholder texts of a channel, product and content type, by text field
 * (`titles`, `subtitles`, …). The admin Templates tab draws its previews with
 * them too, so a preview shows the texts a new page opens with.
 */
export function getDefaultEdits(channelValue, productValue, typeValue, fontOption) {
  const placeholders = PLACEHOLDERS[fontOption] ?? PLACEHOLDERS.en;
  const socialVariantKey = getSocialVariantKey(productValue, typeValue);

  switch (channelValue) {
    case "social":
      return {
        titles: getVariantPlaceholder(placeholders, "title", socialVariantKey),
        subtitles: placeholders.subtitle,
        dates: getVariantPlaceholder(placeholders, "date", socialVariantKey),
      };
    case "releases":
      return {
        titles: placeholders.title,
        subtitles: placeholders.subtitle,
        subtitles3: placeholders.subtitle3,
        dates: placeholders.date,
      };
    case "email":
      return {
        titles: placeholders.title,
        badges: placeholders.button,
        eaps: placeholders.eap,
      };
    case "landingpage":
    case "internal":
    case "partners":
      return {
        titles: placeholders.title,
        subtitles: placeholders.subtitle,
      };
    case "events":
      return {
        titles: placeholders.title,
        subtitles: placeholders.subtitle,
        links: placeholders.link,
        subtitles2: placeholders.subtitle2,
        subtitles3: placeholders.subtitle3,
        links2: placeholders.link2,
        links3: placeholders.link3,
      };
    case "livestream":
      return {
        titles: placeholders.title,
        names: placeholders.name,
        names2: placeholders.name2,
        names3: placeholders.name3,
        names4: placeholders.name4,
        names5: placeholders.name5,
        names6: placeholders.name6,
        subtitles: placeholders.subtitle,
        badges: placeholders.livestream,
        positions: placeholders.position,
        positions2: placeholders.position2,
        dates: placeholders.date,
        times: placeholders.time,
      };
    case "print":
      return {
        titles: placeholders.voucher_title,
        links: placeholders.voucher_link,
        subtitles: placeholders.voucher_subtitle,
        dates: placeholders.voucher_date,
        badges: placeholders.discount,
      };
    case "video":
      return {
        titles: PLACEHOLDERS.en.thumbnails,
      };
    case "ads": {
      const variantKey = getAdsVariantKey(productValue, typeValue);
      return {
        titles: getVariantPlaceholder(placeholders, "title", variantKey),
        subtitles: getVariantPlaceholder(placeholders, "subtitle", variantKey),
      };
    }
    default:
      return null;
  }
}

export function useEnsureDefaultEdits() {
  const {sceneId} = useParams();
  const {fontOption} = useSetting();
  const channelFromParams = useChannelFromParams();
  const productFromParams = useProductFromParams();
  const contentTypeFromParams = useContentTypeFromParams();
  const {
    titles,
    subtitles,
    subtitles2,
    subtitles3,
    badges,
    eaps,
    names,
    names2,
    names3,
    names4,
    names5,
    names6,
    positions,
    positions2,
    dates,
    times,
    links,
    links2,
    links3,
    seedTextRecords,
  } = useEdits();

  // The placeholders follow the offering the route opens, not the channel in
  // the path: JetBrains Air under /social/AIR draws the release banners, so it
  // seeds the release placeholders. See src/data/offering.ts.
  const defaults = useMemo(() => {
    const scope = homeScope(
      channelFromParams?.value,
      contentTypeFromParams?.value,
      productFromParams?.value
    );
    return getDefaultEdits(scope.channel, productFromParams?.value, scope.type, fontOption);
  }, [channelFromParams, productFromParams, contentTypeFromParams, fontOption]);

  // Snapshot of the previously-applied defaults. Lets us detect "field still
  // holds the prior placeholder" → user hasn't customized, so we can rotate it
  // to the new content type's default. If the value differs (user typed),
  // leave it alone. Without this, navigating /ads/JB/ai → /ads/JB/deveco would
  // keep the AI placeholder because the field is no longer empty.
  const prevDefaultsRef = useRef(null);

  useEffect(() => {
    if (sceneId || !defaults) {
      return;
    }

    const prev = prevDefaultsRef.current;
    prevDefaultsRef.current = defaults;

    const fields = [
      {field: "titles", current: titles},
      {field: "subtitles", current: subtitles},
      {field: "subtitles2", current: subtitles2},
      {field: "subtitles3", current: subtitles3},
      {field: "badges", current: badges},
      {field: "eaps", current: eaps},
      {field: "names", current: names},
      {field: "names2", current: names2},
      {field: "names3", current: names3},
      {field: "names4", current: names4},
      {field: "names5", current: names5},
      {field: "names6", current: names6},
      {field: "positions", current: positions},
      {field: "positions2", current: positions2},
      {field: "dates", current: dates},
      {field: "times", current: times},
      {field: "links", current: links},
      {field: "links2", current: links2},
      {field: "links3", current: links3},
    ];

    // Apply the default if the field is empty (initial load) OR if it still
    // holds the previously-applied default (rotation between content types
    // without user input).
    //
    // This render's state only makes the first selection. The reducer checks
    // each seed again against the current state: the default scene of a
    // product switch can load its texts after this render (useInitialScene
    // applies a cached default in the same commit), and a placeholder must
    // not replace them.
    const seeds = fields
      .filter(({field, current}) => {
        const value = defaults[field];
        return (
          value !== undefined &&
          isUntouchedTextRecord(current, prev?.[field]) &&
          current.main !== value
        );
      })
      .map(({field}) => ({field, value: defaults[field], prev: prev?.[field]}));
    if (seeds.length > 0) seedTextRecords(seeds);
  }, [
    sceneId,
    defaults,
    titles,
    subtitles,
    subtitles2,
    subtitles3,
    badges,
    eaps,
    names,
    names2,
    names3,
    names4,
    names5,
    names6,
    positions,
    positions2,
    dates,
    times,
    links,
    links2,
    links3,
    seedTextRecords,
  ]);
}
